import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChartLine,
  faRobot,
  faComments,
  faScroll,
  faUserFriends,
  faShieldAlt,
  faShield,
  faShieldHalved,
  faKey,
  faChevronDown,
  faChevronRight,
  faBuilding,
  faIcons,
  faFileAlt,
  faHospital,
  faUserMd,
  faCalendarAlt,
  faNotesMedical,
  faDiagnoses,
  faFileMedical,
  faMoneyCheckAlt,
  faFileUpload,
  faUsers,
  faUserPlus,
  faHospitalUser,
  faPlus,
  faUserTimes,
  faChartBar,
  faCreditCard,
  faReceipt,
  faMoneyBillWave,
  faMoneyBill,
  faBriefcaseMedical,
  faClipboardList,
  faFileInvoiceDollar,
  faTools,
  faCalendarCheck,
  faTasks,
  faEye,
  faDatabase,
  faTable,
  faTachometerAlt,
  faFileInvoice,
  faCloudUploadAlt,
  faFolderPlus,
  faStethoscope,
  faClinicMedical,
  faUserDoctor,
  faFilePrescription,
  faBookMedical,
  faCoins,
  faIdCardAlt,
  faClipboardCheck,
  faListAlt,
  faSlidersH,
  faFileContract,
  faWrench,
  faBell,
  faBan,
  faSnowflake,
  faSyncAlt,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheckIcon,
  CubeIcon,
  Bars3BottomLeftIcon,
} from "@heroicons/react/24/outline";
import { Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getRequiredScopesForPath } from "../utils/scopeMap";

const SidebarMain = ({ isOpen, onExpandSidebar }) => {
  const [openMenus, setOpenMenus] = useState({});
  const { hasScope } = useAuth();

  // Toggle a menu (keeps other top-level menus closed, preserves existing behavior)
  const toggleMenu = (menuId) => {
    setOpenMenus((prev) => {
      const next = { ...prev };
      const currentlyOpen = !!next[menuId];

      if (currentlyOpen) {
        // close this menu and any of its children
        Object.keys(next).forEach((k) => {
          if (k === menuId || k.startsWith(`${menuId}-`)) next[k] = false;
        });
      } else {
        // close unrelated menus (top-level exclusivity) and open this
        Object.keys(next).forEach((k) => {
          // close menus that are not the new menu and not children/parents
          if (
            k !== menuId &&
            !k.startsWith(`${menuId}-`) &&
            !menuId.startsWith(`${k}-`)
          ) {
            next[k] = false;
          }
        });
        next[menuId] = true;
      }
      return next;
    });

    // if sidebar is collapsed, expand it automatically when user interacts with a menu
    if (!isOpen && typeof onExpandSidebar === "function") {
      onExpandSidebar();
    }
  };

  const navItems = [
    {
      id: "dashboard",
      to: "/dashboard",
      title: "Dashboard",
      icon: faChartLine, // mas bagay sa overview panel
      color: "from-blue-500 to-indigo-600",
      iconColor: "text-blue-600",
    },

    {
      id: "backend-module",
      to: "/backend",
      title: "Backend",
      icon: faTools,
      color: "from-slate-500 to-blue-600",
      iconColor: "text-slate-600",
    },

    {
      id: "backend-logs",
      to: "/backend-logs",
      title: "Backend Logs",
      icon: faListAlt,
      color: "from-blue-500 to-slate-600",
      iconColor: "text-blue-600",
    },

    {
      id: "extract-claims-logs",
      to: "/extract-claims-logs",
      title: "Extract Claims Logs",
      icon: faClipboardList,
      color: "from-emerald-500 to-slate-600",
      iconColor: "text-emerald-600",
    },

    {
      id: "queries",
      to: "/queries",
      title: "Queries",
      icon: faSearch,
      color: "from-emerald-500 to-teal-600",
      iconColor: "text-emerald-600",
    },

    // {
    //   id: "ai-assistant",
    //   to: "/ai-assistant",
    //   title: "AI Assistant", // plain string pa rin
    //   label: (
    //     <>
    //       <Sparkles className="h-6 w-6 text-blue-500 inline-block mr-1" />
    //       AI Assistant
    //     </>
    //   ),
    //   // icon: faSnowflake, //
    //   color: "from-pink-500 to-rose-600",
    //   iconColor: "text-rose-600",
    // },

    {
      id: "leave-ot-ut",
      to: "/hris-redirect",
      title: "Leaves/OT/UT",
      icon: "📅",
      color: "from-emerald-500 to-green-400",
      iconColor: "text-emerald-600",
      description: "File and manage leaves, overtime, and undertime",
      badge: "External",
      external: true,
      externalUrl: "https://staging-hris.amaphil.com.ph/login",
    },

    {
      id: "reports",
      to: "/reports",
      title: "Reports",
      icon: faChartBar,
      color: "from-blue-500 to-cyan-400",
      iconColor: "text-blue-600",
      description: "Generate operational reports",
      badge: "V2",
    },

    {
      id: "messages",
      to: "/messages",
      title: "Messages",
      icon: faComments,
      color: "from-sky-500 to-blue-600",
      iconColor: "text-sky-600",
      description: "SMS queues, approvals, and feedbacks",
    },

    {
      id: "medical",
      title: "Medical",
      icon: faStethoscope, // mas representative sa medical
      color: "from-teal-500 to-cyan-600",
      iconColor: "text-teal-600",
      subItems: [
        {
          id: "medical-provider",
          to: "/medical/provider",
          title: "Providers",
          icon: faClinicMedical, // para sa hospitals/clinics
          iconColor: "text-cyan-500",
        },
        {
          id: "medical-provider-claims-submission-settings",
          to: "/medical/provider-claims-submission-settings",
          title: "Submission of Claims (Contract) Days Settings",
          icon: faSlidersH,
          iconColor: "text-cyan-500",
        },
        {
          id: "physician",
          to: "/physician",
          title: "Physician",
          icon: faUserDoctor, // updated doctor icon
          iconColor: "text-cyan-500",
        },
        {
          id: "physician-batch-upload-group",
          title: "Physician Batch Upload",
          icon: faFileUpload,
          color: "from-teal-500 to-cyan-600",
          iconColor: "text-cyan-500",
          subItems: [
            {
              id: "physician-batch-upload",
              to: "/physician-batch-upload",
              title: "Physician Batch Upload",
              icon: faFileUpload,
              iconColor: "text-cyan-500",
            },
            {
              id: "history-physician-batch-upload",
              to: "/history-physician-batch-upload",
              title: "View Uploaded File History",
              icon: faListAlt,
              iconColor: "text-cyan-500",
            },
            {
              id: "processed-physician-batch-upload",
              to: "/processed-physician-batch-upload",
              title: "View Processed Data",
              icon: faClipboardCheck,
              iconColor: "text-cyan-500",
            },
          ],
        },
        // {
        //   id: "ape-scheduler",
        //   to: "/ape-scheduler",
        //   title: "APE Scheduler",
        //   icon: CubeDiagnosis Icon, // mas bagay sa scheduling
        //   iconColor: "text-cyan-500",
        //   isHero: true,
        // },

        {
          id: "ape-scheduler",
          title: "APE Scheduler",
          icon: "⏰", // Clock/Alarm - represents scheduling
          color: "from-blue-500 to-cyan-600",
          iconColor: "text-blue-600",
          subItems: [
            {
              id: "ape-scheduler-setup",
              to: "/ape-scheduler-setup",
              title: "APE Scheduler Setup",
              icon: "🛠️", // Tools for setup
              iconColor: "text-cyan-500",
            },
            {
              id: "ape-pdf-history",
              to: "/ape-pdf-history",
              title: "APE Generated PDF History",
              icon: "📑", // Document set for history
              iconColor: "text-blue-400",
            },
          ],
        },

        {
          id: "cpt",
          to: "/cpt",
          title: "CPT Rates (Procedures)",
          icon: faFilePrescription, // bagay sa medical codes
          iconColor: "text-cyan-500",
        },
        {
          id: "cpt-batch-upload-group",
          title: "CPT Batch Upload",
          icon: faFileUpload,
          color: "from-teal-500 to-cyan-600",
          iconColor: "text-cyan-500",
          subItems: [
            {
              id: "cpt-batch-upload",
              to: "/cpt-batch-upload",
              title: "CPT Batch Upload",
              icon: faFileUpload,
              iconColor: "text-cyan-500",
            },
            {
              id: "history-cpt-batch-upload",
              to: "/history-cpt-batch-upload",
              title: "View Uploaded File History",
              icon: faListAlt,
              iconColor: "text-cyan-500",
            },
            {
              id: "processed-cpt-batch-upload",
              to: "/processed-cpt-batch-upload",
              title: "View Processed Data",
              icon: faClipboardCheck,
              iconColor: "text-cyan-500",
            },
          ],
        },
        {
          id: "icd",
          to: "/icd",
          title: "ICD Codes (Diagnosis)",
          icon: faBookMedical, // para sa coding manuals
          iconColor: "text-cyan-500",
        },
        {
          id: "rvu",
          to: "/rvu",
          // title: "RUV",
          title: "RVS Codes (Relative Value Scale)",
          icon: faCoins, // bagay sa computation/valuation
          iconColor: "text-cyan-500",
        },
      ],
    },
    {
      id: "corporate",
      to: "/corporate",
      title: "Corporate",
      icon: faBuilding, // sakto na
      color: "from-purple-500 to-fuchsia-600",
      iconColor: "text-fuchsia-600",
    },

    {
      id: "plan-and-coverage-benefits-setup",
      title: "Plan & Coverage",
      icon: ShieldCheckIcon, // mas bagay sa insurance/coverage
      color: "from-orange-500 to-amber-600",
      iconColor: "text-amber-600",
      isHero: true,
      subItems: [
        {
          id: "plan-and-coverage-benefits-setup",
          to: "/plan-and-coverage-benefits-setup",
          title: "Plan & Coverage Setup",
          icon: faSlidersH, // para sa configuration/setup
          iconColor: "text-amber-500",
        },
        {
          id: "plan-and-coverage-benefits-view",
          to: "/plan-and-coverage-benefits-view",
          title: "Plan & Coverage Viewing",
          icon: faFileContract, // mas bagay sa viewing/search
          iconColor: "text-amber-500",
        },
      ],
    },

    {
      id: "members",
      title: "Members Module",
      color: "from-blue-500 to-fuchsia-600",
      icon: "👥",
      iconColor: "text-blue-600",
      subItems: [
        {
          id: "members",
          to: "/members",
          title: "Member List", // single person
          icon: "👤",
          color: "from-blue-500 to-fuchsia-600",
          iconColor: "text-blue-600",
        },
        {
          id: "member-employee-id-generator",
          to: "/member-employee-id-generator",
          title: "Employee ID Generator",
          icon: faKey,
          color: "from-blue-500 to-fuchsia-600",
          iconColor: "text-blue-600",
        },
        {
          id: "member-batch-enrollment-group",
          title: "Batch Member Enrollment",
          icon: "📤",
          color: "from-blue-500 to-fuchsia-600",
          iconColor: "text-blue-600",
          subItems: [
            {
              id: "member-batch-enrollment",
              to: "/member-batch-enrollment",
              title: "Enrollment Uploading",
              icon: "📤",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "history-member-batch-enrollment",
              to: "/history-member-batch-enrollment",
              title: "View Uploaded File History",
              icon: "📜",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "processed-member-batch-enrollment",
              to: "/processed-member-batch-enrollment",
              title: "View Processed Data",
              icon: "☑️",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
          ],
        },
        {
          id: "member-batch-renewal-group",
          title: "Batch Renewal Upload",
          icon: "📤",
          color: "from-blue-500 to-fuchsia-600",
          iconColor: "text-blue-600",
          subItems: [
            {
              id: "member-batch-renewal-upload",
              to: "/member-batch-renewal-upload",
              title: "Renewal Upload",
              icon: "📤",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "history-member-batch-renewal-upload",
              to: "/history-member-batch-renewal-upload",
              title: "View Uploaded File History",
              icon: "📜",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "processed-member-batch-renewal-upload",
              to: "/processed-member-batch-renewal-upload",
              title: "View Processed Data",
              icon: "☑️",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
          ],
        },
        {
          id: "member-batch-activation-group",
          title: "Batch Member Activation",
          icon: "📤",
          color: "from-blue-500 to-fuchsia-600",
          iconColor: "text-blue-600",
          subItems: [
            {
              id: "member-batch-activation-upload",
              to: "/member-batch-activation-upload",
              title: "Activation Upload",
              icon: "📤",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "history-member-batch-activation-upload",
              to: "/history-member-batch-activation-upload",
              title: "View Uploaded File History",
              icon: "📜",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "processed-member-batch-activation-upload",
              to: "/processed-member-batch-activation-upload",
              title: "View Processed Data",
              icon: "☑️",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
          ],
        },
        {
          id: "member-batch-deactivation-group",
          title: "Batch Member Deactivation",
          icon: "📤",
          color: "from-blue-500 to-fuchsia-600",
          iconColor: "text-blue-600",
          subItems: [
            {
              id: "member-batch-deactivation-upload",
              to: "/member-batch-deactivation-upload",
              title: "Deactivation Upload",
              icon: "📤",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "history-member-batch-deactivation-upload",
              to: "/history-member-batch-deactivation-upload",
              title: "View Uploaded File History",
              icon: "📜",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "processed-member-batch-deactivation-upload",
              to: "/processed-member-batch-deactivation-upload",
              title: "View Processed Data",
              icon: "☑️",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
          ],
        },
        {
          id: "member-batch-change-plan-group",
          title: "Batch Change Plan",
          icon: "📤",
          color: "from-blue-500 to-fuchsia-600",
          iconColor: "text-blue-600",
          subItems: [
            {
              id: "member-batch-change-plan-upload",
              to: "/member-batch-change-plan-upload",
              title: "Change Plan Upload",
              icon: "📤",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "history-member-batch-change-plan-upload",
              to: "/history-member-batch-change-plan-upload",
              title: "View Uploaded File History",
              icon: "📜",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "processed-member-batch-change-plan-upload",
              to: "/processed-member-batch-change-plan-upload",
              title: "View Processed Data",
              icon: "☑️",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
          ],
        },
        {
          id: "member-batch-text-alert-mobile-update-group",
          title: "Batch Text Alert/Mobile# Update",
          icon: "📤",
          color: "from-blue-500 to-fuchsia-600",
          iconColor: "text-blue-600",
          subItems: [
            {
              id: "member-batch-text-alert-mobile-update-upload",
              to: "/member-batch-text-alert-mobile-update-upload",
              title: "Text Alert/Mobile Upload",
              icon: "📤",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "history-member-batch-text-alert-mobile-update-upload",
              to: "/history-member-batch-text-alert-mobile-update-upload",
              title: "View Uploaded File History",
              icon: "📜",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
            {
              id: "processed-member-batch-text-alert-mobile-update-upload",
              to: "/processed-member-batch-text-alert-mobile-update-upload",
              title: "View Processed Data",
              icon: "☑️",
              color: "from-blue-500 to-fuchsia-600",
              iconColor: "text-blue-600",
            },
          ],
        },
      ],
    },

    // {
    //   id: "transaction",
    //   title: "Transaction",
    //   icon: ShieldCheckIcon, // mas bagay sa insurance/coverage
    //   color: "from-orange-500 to-amber-600",
    //   iconColor: "text-amber-600",
    //   isHero: true,
    //   subItems: [
    //     {
    //       id: "transaction-generator",
    //       to: "/transaction-generator",
    //       title: "Transaction Generator",
    //       icon: faSlidersH, // para sa configuration/setup
    //       iconColor: "text-amber-500",
    //     },
    //   ],
    // },

    {
      id: "transaction",
      title: "Transactions",
      color: "from-orange-500 to-amber-600",
      icon: "🩺",
      iconColor: "text-amber-600",
      subItems: [
        {
          id: "transactions",
          to: "/transactions",
          title: "(All TRN) Transaction List", // Transaction List
          icon: "📑",
          iconColor: "text-amber-500",
        },
        {
          id: "tracker",
          to: "/tracker",
          title: "Tracker",
          icon: "🎯",
          iconColor: "text-amber-500",
        },
        {
          id: "loa-issuer-lookup",
          to: "/transactions/loa-issuer-lookup",
          title: "LOA Issuer Lookup",
          icon: faSearch,
          iconColor: "text-amber-500",
          badge: "New",
          badgeClass: "bg-red-600 text-white",
        },
        {
          id: "generate-loa-transaction",
          to: "/generate-loa-transaction",
          title: "Generate LOA Transaction", // creating new LOA
          icon: "🆕",
          iconColor: "text-amber-500",
        },
        {
          id: "transaction-verification",
          to: "/transaction-verification",
          title: "Admission Verification", // In-Patient/Maternity Admission Verification
          icon: "🏥",
          iconColor: "text-amber-500",
        },
        {
          id: "transaction-discharge",
          to: "/transaction-discharge",
          title: "Discharge Processing", // In-Patient/Maternity Admission Discharge
          icon: "🚼",
          iconColor: "text-amber-500",
        },

        {
          id: "transaction-reimbursement-list",
          to: "/transaction-reimbursement-list",
          title: "Reimbursement List",
          // icon: "💰",
          icon: "🏷️",
          iconColor: "text-amber-500",
        },
        {
          id: "transaction-reimbursement-add",
          to: "/transaction-reimbursement-add",
          title: "Add New Reimbursement",
          // icon: "💸",
          icon: "📄",
          iconColor: "text-amber-500",
        },
      ],
    },

    // {
    //   id: "provider-payment",
    //   title: "Provider Payment",
    //   icon: "💳",
    //   color: "from-sky-500 to-cyan-600",
    //   iconColor: "text-sky-600",
    //   subItems: [
    //     {
    //       id: "transaction-reimbursement-dpr",
    //       to: "/transaction-reimbursement-dpr",
    //       title: "Detailed Payment Report",
    //       icon: "📊",
    //       iconColor: "text-sky-500",
    //     },
    //     {
    //       id: "dpr-validation-dashboard",
    //       to: "/dpr-validation-dashboard",
    //       title: "(DPR) Validation Dashboard",
    //       icon: "📈",
    //       iconColor: "text-sky-500",
    //     },
    //       {
    //         id: "dpr-validation-upload",
    //         to: "/dpr-validation-upload",
    //         title: "(DPR) Upload Validation CSV",
    //         icon: "📤",
    //         iconColor: "text-sky-500",
    //       },
    //       {
    //         id: "dpr-validation-upload-history",
    //         to: "/dpr-validation-upload-history",
    //         title: "(DPR) Upload Validation CSV History",
    //         icon: "📜",
    //         iconColor: "text-sky-500",
    //       },
    //       {
    //         id: "dpr-validation-upload-processed",
    //         to: "/dpr-validation-upload-processed",
    //         title: "(DPR) Upload Validation CSV Processed",
    //         icon: "☑️",
    //         iconColor: "text-sky-500",
    //       },
    //       {
    //         id: "settlement-list",
    //         to: "/settlement-list",
    //         title: "Settlement List",
    //         icon: "💰",
    //         iconColor: "text-sky-500",
    //       },
    //       {
    //         id: "settlement-history",
    //         to: "/settlement-history",
    //         title: "Settlement History",
    //         icon: "📜",
    //         iconColor: "text-sky-500",
    //       },
    //   ],
    // },

    {
      id: "claims",
      title: "Claims",
      icon: faFileInvoice, // mas general icon for claims processing
      color: "from-green-500 to-emerald-600",
      iconColor: "text-emerald-600",
      subItems: [
        {
          id: "claims-batch-received-scanned",
          to: "/claims/receiving",
          title: "Received/Scanned Uploading",
          icon: faCloudUploadAlt, // mas akma sa uploading
          iconColor: "text-emerald-500",
        },
        {
          id: "claims-receiving-excluded-logs",
          to: "/claims/receiving/excluded-logs",
          title: "Receiving Excluded Logs",
          icon: faListAlt,
          iconColor: "text-emerald-500",
        },
        {
          id: "claims-batch-registration",
          to: "/claims/batches",
          title: "Batch Registration",
          icon: faFolderPlus, // bagay sa pag-register ng batch
          iconColor: "text-emerald-500",
        },
        {
          id: "claims-stale-checker",
          to: "/claims/stale-checker",
          title: "Stale Checker",
          icon: faSearch,
          iconColor: "text-emerald-500",
        },
        {
          id: "claims-processing",
          to: "/claims/processing",
          title: "Claims Processing",
          icon: faFileInvoiceDollar, // processing/billing related
          iconColor: "text-emerald-500",
        },
        {
          id: "claims-rtp-entry",
          to: "/claims/rtp-entry",
          title: "For RTP Entry Module",
          icon: faClipboardCheck,
          iconColor: "text-emerald-500",
        },
        {
          id: "claims-rtp-report-query-request",
          to: "/claims/rtp-report-query-request",
          title: "For RTP Report Request",
          icon: faTable,
          iconColor: "text-teal-500",
          badge: "New",
          badgeClass: "bg-red-600 text-white",
        },
        {
          id: "claims-rtp-downloaded-form-history",
          to: "/claims/rtp-downloaded-form-history",
          title: "For RTP Downloaded Form",
          icon: faFileAlt,
          iconColor: "text-emerald-500",
        },
        {
          id: "claims-physician-not-yet-enrolled",
          to: "/claims/physician-not-yet-enrolled",
          title: "Physician Not Yet Enrolled",
          icon: faUserMd,
          iconColor: "text-emerald-500",
          badge: "New",
          badgeClass: "bg-red-600 text-white",
        },
        {
          id: "claims-rtp-approvals",
          to: "/claims/rtp-final-approvals",
          title: "For RTP Final Approval",
          icon: faClipboardCheck,
          iconColor: "text-emerald-500",
        },
        {
          id: "claims-extract-queue",
          to: "/claims/extract-queue",
          title: "Extract Claims Queue",
          icon: faClipboardList,
          iconColor: "text-emerald-500",
        },
        {
          id: "claims-upload-queue-loa",
          to: "/claims/upload-queue",
          title: "Upload Queue - LOA",
          icon: faSyncAlt,
          iconColor: "text-emerald-500",
        },
        {
          id: "claims-upload-queue-soa",
          to: "/claims/upload-queue-soa",
          title: "Upload Queue - SOA",
          icon: faSyncAlt,
          iconColor: "text-emerald-500",
        },
        {
          id: "claims-already-batch-audit-logs",
          to: "/claims/already-batch-audit-logs",
          title: "Already Batch Audit Logs",
          icon: faClipboardCheck,
          iconColor: "text-emerald-500",
        },
      ],
    },

    {
      id: "provider-payment",
      title: "Provider Payment",
      icon: "💳",
      color: "from-sky-500 to-cyan-600",
      iconColor: "text-sky-600",
      subItems: [
        {
          id: "transaction-reimbursement-dpr",
          to: "/transaction-reimbursement-dpr",
          title: "Detailed Payment Report",
          icon: "📊",
          iconColor: "text-sky-500",
        },
        {
          id: "transaction-reimbursement-dpr-all",
          to: "/transaction-reimbursement-dpr-all",
          title: "Detailed Payment Report (ALL)",
          icon: faListAlt,
          iconColor: "text-red-500",
        },
        {
          id: "batch-add-holiday-module",
          title: "Batch Add Holiday",
          icon: faCalendarAlt,
          iconColor: "text-sky-500",
          subItems: [
            {
              id: "batch-add-holiday-list",
              to: "/batch-add-holiday-list",
              title: "List",
              icon: faListAlt,
              iconColor: "text-sky-500",
            },
            {
              id: "batch-add-holiday-upload",
              to: "/batch-add-holiday-upload",
              title: "Add Holiday Upload",
              icon: faFileUpload,
              iconColor: "text-sky-500",
            },
            {
              id: "history-batch-add-holiday-upload",
              to: "/history-batch-add-holiday-upload",
              title: "View Uploaded File History",
              icon: faListAlt,
              iconColor: "text-sky-500",
            },
            {
              id: "processed-batch-add-holiday-upload",
              to: "/processed-batch-add-holiday-upload",
              title: "View Processed Data",
              icon: faClipboardCheck,
              iconColor: "text-sky-500",
            },
          ],
        },
        {
          id: "validation-module",
          title: "Validation Module",
          icon: faClipboardCheck,
          iconColor: "text-sky-500",
          subItems: [
            {
              id: "dpr-validation-dashboard",
              to: "/dpr-validation-dashboard",
              title: "Dashboard",
              icon: faChartLine,
              iconColor: "text-sky-500",
            },
            {
              id: "dpr-validation-upload",
              to: "/dpr-validation-upload",
              title: "Upload Validation CSV",
              icon: faFileUpload,
              iconColor: "text-sky-500",
            },
            {
              id: "dpr-validation-upload-history",
              to: "/dpr-validation-upload-history",
              title: "Upload Validation CSV History",
              icon: faListAlt,
              iconColor: "text-sky-500",
            },
            {
              id: "dpr-validation-upload-processed",
              to: "/dpr-validation-upload-processed",
              title: "Upload Validation CSV Processed",
              icon: faClipboardCheck,
              iconColor: "text-sky-500",
            },
            {
              id: "dpr-validation-upload-ai-summary-history",
              to: "/dpr-validation-upload-ai-summary-history",
              title: "Upload Validation AI Summary History",
              icon: faRobot,
              iconColor: "text-sky-500",
            },
            {
              id: "dpr-validation-support",
              to: "/dpr-validation-support",
              title: "DPR Validation Support",
              icon: faWrench,
              iconColor: "text-sky-500",
            },
          ],
        },
        {
          id: "find-data-using-prn",
          to: "/find-data-using-prn",
          title: "Find Data Using PRN Number",
          icon: "🔍",
          iconColor: "text-sky-500",
        },
        {
          id: "credit-file",
          to: "/credit-file",
          title: "Credit File",
          icon: faFileInvoice,
          iconColor: "text-sky-500",
        },
        {
          id: "provider-bank-validation",
          to: "/provider-bank-validation",
          title: "Provider / Physician Bank Validation",
          icon: faMoneyCheckAlt,
          iconColor: "text-sky-500",
        },
        {
          id: "credit-advise-module",
          title: "Credit Advise Upload",
          icon: faFileUpload,
          iconColor: "text-sky-500",
          subItems: [
            {
              id: "credit-advise",
              to: "/credit-advise",
              title: "Upload Credit Advise CSV",
              icon: faFileUpload,
              iconColor: "text-sky-500",
            },
            {
              id: "credit-advise-history",
              to: "/credit-advise-history",
              title: "Credit Advise History",
              icon: faListAlt,
              iconColor: "text-sky-500",
            },
            {
              id: "credit-advise-processed",
              to: "/credit-advise-processed/:uploadId",
              title: "Credit Advise Processed",
              icon: faClipboardCheck,
              iconColor: "text-sky-500",
            },
          ],
        },
        {
          id: "settlement-email-outbox",
          to: "/settlement-email-outbox",
          title: "Settlement Email Outbox",
          icon: faSyncAlt,
          iconColor: "text-sky-500",
        },
        {
          id: "settlement-list",
          to: "/settlement-list",
          title: "Settlement List",
          icon: "💰",
          iconColor: "text-sky-500",
        },
        {
          id: "settlement-history",
          to: "/settlement-history",
          title: "Settlement History",
          icon: "📜",
          iconColor: "text-sky-500",
        },
        {
          id: "settlement-batch-print-pis-form2307",
          to: "/settlement-batch-print-pis-form2307",
          title: "Batch Printing PIS/Form2307",
          icon: faFileAlt,
          iconColor: "text-sky-500",
        },
        {
          id: "batch-regenerate-pis-form2307-group",
          title: "Batch Regenerate PIS/Form2307",
          icon: faSyncAlt,
          iconColor: "text-sky-500",
          subItems: [
            {
              id: "batch-regenerate-pis-form2307",
              to: "/batch-regenerate-pis-form2307",
              title: "Upload PRN File",
              icon: faFileUpload,
              iconColor: "text-sky-500",
            },
            {
              id: "batch-regenerate-pis-form2307-history",
              to: "/batch-regenerate-pis-form2307-history",
              title: "Regenerate History",
              icon: faListAlt,
              iconColor: "text-sky-500",
            },
            {
              id: "batch-regenerate-pis-form2307-processed",
              to: "/batch-regenerate-pis-form2307-processed/:uploadId",
              title: "Regenerate Processed",
              icon: faClipboardCheck,
              iconColor: "text-sky-500",
            },
          ],
        },
        {
          id: "batch-upload-settlement",
          title: "Batch Upload Settlement",
          icon: faFileUpload,
          iconColor: "text-sky-500",
          subItems: [
            {
              id: "settlement-batch-upload",
              to: "/settlement-batch-upload",
              title: "Upload Settlement CSV",
              icon: faFileUpload,
              iconColor: "text-sky-500",
            },
            {
              id: "settlement-batch-upload-history",
              to: "/settlement-batch-upload-history",
              title: "Settlement CSV History",
              icon: faListAlt,
              iconColor: "text-sky-500",
            },
            {
              id: "settlement-batch-upload-processed",
              to: "/settlement-batch-upload-processed/:uploadId",
              title: "Settlement CSV Processed",
              icon: faClipboardCheck,
              iconColor: "text-sky-500",
            },
          ],
        },
        {
          id: "batch-check-status-upload",
          title: "Batch Check Status Upload",
          icon: faFileUpload,
          iconColor: "text-sky-500",
          subItems: [
            {
              id: "check-status-batch-upload",
              to: "/check-status-batch-upload",
              title: "Upload Check Status CSV",
              icon: faFileUpload,
              iconColor: "text-sky-500",
            },
            {
              id: "check-status-batch-upload-history",
              to: "/check-status-batch-upload-history",
              title: "Check Status CSV History",
              icon: faListAlt,
              iconColor: "text-sky-500",
            },
            {
              id: "check-status-batch-upload-processed",
              to: "/check-status-batch-upload-processed/:uploadId",
              title: "Check Status CSV Processed",
              icon: faClipboardCheck,
              iconColor: "text-sky-500",
            },
          ],
        },
        {
          id: "batch-deposit-slip",
          title: "Batch Deposit Slip",
          icon: faFileInvoiceDollar,
          iconColor: "text-sky-500",
          subItems: [
            {
              id: "deposit-slip-batch-upload",
              to: "/deposit-slip-batch-upload",
              title: "Upload Deposit Slip CSV",
              icon: faFileUpload,
              iconColor: "text-sky-500",
            },
            {
              id: "deposit-slip-batch-upload-history",
              to: "/deposit-slip-batch-upload-history",
              title: "Deposit Slip CSV History",
              icon: faListAlt,
              iconColor: "text-sky-500",
            },
            {
              id: "deposit-slip-batch-upload-processed",
              to: "/deposit-slip-batch-upload-processed/:uploadId",
              title: "Deposit Slip CSV Processed",
              icon: faClipboardCheck,
              iconColor: "text-sky-500",
            },
          ],
        },
        {
          id: "batch-depo-or-files",
          title: "Deposit Slip / OR Files Upload",
          icon: faCloudUploadAlt,
          iconColor: "text-sky-500",
          subItems: [
            {
              id: "depo-or-batch-upload",
              to: "/depo-or-batch-upload",
              title: "Batch Upload",
              icon: faFileUpload,
              iconColor: "text-sky-500",
            },
            {
              id: "depo-or-batch-upload-history",
              to: "/depo-or-batch-upload-history",
              title: "Upload History",
              icon: faListAlt,
              iconColor: "text-sky-500",
            },
            {
              id: "depo-or-batch-upload-processed",
              to: "/depo-or-batch-upload-processed/:uploadId",
              title: "Upload Processed",
              icon: faClipboardCheck,
              iconColor: "text-sky-500",
            },
          ],
        },
        {
          id: "provider-delivery-address-label",
          to: "/provider-delivery-address-label",
          title: "Provider Delivery Address Label",
          icon: faFileAlt,
          iconColor: "text-sky-500",
        },
      ],
    },

    {
      id: "provider-access",
      to: "/provider-access",
      title: "Provider Access (Blocking)",
      icon: faBan, // mas bagay for blocking/restricting
      color: "from-pink-500 to-rose-600",
      iconColor: "text-rose-600",
    },

    {
      id: "administrator",
      title: "System Admin",
      icon: "🔐", // mas bagay for reference tables
      color: "from-sky-500 to-blue-600",
      iconColor: "text-sky-600",
      subItems: [
        {
          id: "banners",
          to: "/banners",
          title: "Banner Alert Settings",
          icon: faBell, // mas akma para sa alerts
          color: "from-pink-500 to-rose-600",
          iconColor: "text-rose-600",
        },
        {
          id: "activity-logs",
          to: "/activity-logs",
          title: "System Activity Logs",
          icon: "🖥️",
          color: "from-pink-500 to-blue-600",
          iconColor: "text-sky-600",
        },
        {
          id: "curl-request-response-logs",
          to: "/system-admin/curl-request-response-logs",
          title: "cURL Request Logs",
          icon: faListAlt,
          color: "from-indigo-500 to-blue-600",
          iconColor: "text-indigo-500",
        },
        {
          id: "system-users",
          to: "/system-users",
          // title: "System Changelog History",
          title: "System User Management",
          icon: "👥",
          color: "from-pink-500 to-blue-600",
          iconColor: "text-sky-600",
        },
        {
          id: "system-settings-v2",
          to: "/system-settings-v2",
          title: "System Settings V2",
          icon: faSlidersH,
          color: "from-indigo-500 to-sky-600",
          iconColor: "text-indigo-500",
        },
        {
          id: "upload-s3-public-share-token",
          to: "/system-admin/upload-s3-public-share-token",
          title: "Upload S3 Public Share Token",
          icon: "🔗",
          color: "from-cyan-500 to-blue-600",
          iconColor: "text-cyan-600",
        },
        {
          id: "dynamic-table-update",
          to: "/system-admin/dynamic-table-update",
          title: "Dynamic Table Update",
          icon: faDatabase,
          color: "from-violet-500 to-purple-600",
          iconColor: "text-violet-600",
        },
        {
          id: "vector-database",
          to: "/vector-database",
          title: "Vector Database Implementation",
          icon: "🗄️",
          color: "from-pink-500 to-blue-600",
          iconColor: "text-sky-600",
        },

        {
          id: "auxiliary-tools",
          to: "/auxiliary-tools",
          title: "Auxiliary Tools",
          icon: faWrench, // mas akma kaysa faTools
          color: "from-pink-500 to-rose-600",
          iconColor: "text-rose-600",
        },

        {
          id: "ref-master-table-list",
          title: "Reference Master Table List",
          icon: "🔲", // mas bagay for reference tables
          color: "from-sky-500 to-blue-600",
          iconColor: "text-sky-600",
          subItems: [
            {
              id: "ref-coverage-master",
              to: "/ref-master-table-list/ref-coverage-master",
              title: "ref_coverage_master",
              icon: "📄",
              iconColor: "text-sky-500",
            },
            {
              id: "ref-benefit-master",
              to: "/ref-master-table-list/ref-benefit-master",
              title: "ref_benefit_master",
              icon: "📄",
              iconColor: "text-sky-500",
            },
          ],
        },



        {
          id: "google-sign-in-sample",
          to: "/google-sign-in-sample",
          title: "Google Sign-In Sample",
          icon: faWrench, // mas akma kaysa faTools
          color: "from-pink-500 to-rose-600",
          iconColor: "text-rose-600",
        },
      ],
    },

    {
      id: "change-logs",
      to: "/change-logs",
      // title: "System Changelog History",
      title: "System v2.0 Updates",
      icon: "📋",
      color: "from-pink-500 to-blue-600",
      iconColor: "text-sky-600",
    },
  ];

  const subMenuVariants = {
    closed: {
      height: 0,
      opacity: 0,
      transition: { duration: 0.22, ease: "easeOut" },
    },
    open: {
      height: "auto",
      opacity: 1,
      transition: { duration: 0.28, ease: "easeIn" },
    },
  };

  // Render a menu item (recursive for subitems)
  const renderMenuItem = (item, level = 0, parentId = "", parentColor = "") => {
    const hasSub = item.subItems && item.subItems.length > 0;
    const menuId = parentId ? `${parentId}-${item.id}` : item.id;
    const isOpenMenu = !!openMenus[menuId];
    const gradient = item.color || parentColor;

    const iconSize = level > 0 ? "h-7 w-7" : "h-8 w-8";
    const iconFontSize = level > 0 ? "h-3.5 w-3.5" : "h-4 w-4";
    const emojiSize = level > 0 ? "text-sm" : "text-base";
    const heroSize = level > 0 ? "h-4 w-4" : "h-5 w-5";

    const iconWrapperBase = `flex items-center justify-center ${iconSize} rounded-md transition-all duration-200`;

    const navButtonBase =
      "relative flex items-center gap-2.5 w-full text-xs font-medium rounded-md group transition-all duration-200";

    // When collapsed, show icon-only with tooltip (title)
    if (item.to) {
      return (
        <div key={menuId} className="relative">
          <NavLink
            to={item.to}
            title={item.title}
            className={({ isActive }) =>
              `${navButtonBase} ${isOpen ? "px-2.5 py-1.5" : "p-1.5 my-0.5"} ${
                isOpen ? "" : "justify-center"
              } ${
                isActive
                  ? `bg-gradient-to-r ${gradient} text-white shadow-sm`
                  : level > 0
                  ? "text-gray-600 hover:bg-white/80 hover:text-gray-900"
                  : "text-gray-700 hover:bg-gray-100/80"
              }`
            }
            style={isOpen && level > 0 ? { paddingLeft: `${8 + level * 12}px` } : undefined}
          >
            <div
              className={`${iconWrapperBase} flex-shrink-0 ${
                item.iconColor || "text-gray-500"
              }`}
              aria-hidden
            >
              {item.isHero ? (
                <item.icon className={heroSize} />
              ) : typeof item.icon === "string" ? (
                <span className={emojiSize}>{item.icon}</span>
              ) : (
                <FontAwesomeIcon icon={item.icon} className={iconFontSize} />
              )}
            </div>

            {isOpen && (
              <div className="flex-1 flex items-center min-w-0">
                <span
                  className={`leading-tight ${
                    level > 0 ? "whitespace-normal break-words" : "truncate"
                  }`}
                >
                  {item.label ?? item.title}
                </span>
              </div>
            )}

            {item.badge && (
              <span
                className={`absolute ${
                  isOpen ? "right-1.5 top-0.5" : "-right-1 -top-1"
                } rounded-full px-1.5 py-0.5 text-[9px] font-bold leading-none shadow-sm ${
                  item.badgeClass || "bg-gray-900 text-white"
                }`}
              >
                {item.badge}
              </span>
            )}
          </NavLink>
        </div>
      );
    }

    // Parent menu (no "to") - toggles submenu
    return (
      <div key={menuId} className="relative">
        <button
          type="button"
          onClick={() => hasSub && toggleMenu(menuId)}
          title={item.title}
          className={`w-full ${navButtonBase} ${
            isOpen ? "px-2.5 py-1.5" : "p-1.5 my-0.5"
          } ${
            isOpen
              ? isOpenMenu
                ? `bg-gradient-to-r ${gradient} text-white shadow-sm`
                : "text-gray-700 hover:bg-gray-100/80"
              : "justify-center"
          }`}
          style={isOpen && level > 0 ? { paddingLeft: `${8 + level * 12}px` } : undefined}
        >
          <div
            className={`${iconWrapperBase} flex-shrink-0 ${
              isOpenMenu ? "text-white" : ""
            } ${item.iconColor || "text-gray-500"}`}
            aria-hidden
          >
            {item.isHero ? (
              <item.icon className={heroSize} />
            ) : typeof item.icon === "string" ? (
              <span className={emojiSize}>{item.icon}</span>
            ) : (
              <FontAwesomeIcon icon={item.icon} className={iconFontSize} />
            )}
          </div>

          {isOpen && (
            <>
              <div className="flex-1 min-w-0 text-left">
                <span
                  className={`text-xs font-semibold leading-tight ${
                    isOpenMenu ? "text-white" : "text-gray-800"
                  } ${level > 0 ? "whitespace-normal break-words" : "truncate"}`}
                >
                  {item.label ?? item.title}
                </span>
              </div>

              {hasSub && (
                <motion.span
                  initial={{ rotate: 0 }}
                  animate={{ rotate: isOpenMenu ? 180 : 0 }}
                  transition={{ duration: 0.18 }}
                  className={`text-[10px] flex-shrink-0 ${
                    isOpenMenu ? "text-white/90" : "text-gray-400"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={isOpenMenu ? faChevronDown : faChevronRight}
                  />
                </motion.span>
              )}
            </>
          )}
        </button>

        {/* Submenu with left border indicator */}
        {hasSub && (
          <div className={`${isOpen ? "" : "hidden"}`}>
            <AnimatePresence initial={false}>
              {isOpenMenu && (
                <motion.div
                  initial="closed"
                  animate="open"
                  exit="closed"
                  variants={subMenuVariants}
                  className="overflow-hidden"
                >
                  <div className="relative ml-5 mt-0.5 mb-0.5">
                    {/* Vertical connector line */}
                    <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-gray-300 via-gray-200 to-transparent rounded-full" />
                    <div className="space-y-0.5 pl-3">
                      {item.subItems.map((sub) =>
                        renderMenuItem(sub, level + 1, menuId, gradient)
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    );
  };

  const getItemScopes = (item) => {
    if (item.requiredScopes) return item.requiredScopes;
    if (item.to) return getRequiredScopesForPath(item.to);
    return [];
  };

  const isParameterizedPath = (path) => {
    return typeof path === "string" && /\/:[^/]+/.test(path);
  };

  const filterNavItems = (items) => {
    return items
      .map((item) => {
        if (item.to && isParameterizedPath(item.to)) {
          return null;
        }

        if (item.subItems && item.subItems.length > 0) {
          const filteredSubItems = filterNavItems(item.subItems);
          const isAllowedSelf = item.to ? hasScope(getItemScopes(item)) : false;
          if (!isAllowedSelf && filteredSubItems.length === 0) {
            return null;
          }
          return { ...item, subItems: filteredSubItems };
        }

        if (item.to && !hasScope(getItemScopes(item))) {
          return null;
        }

        return item;
      })
      .filter(Boolean);
  };

  const visibleItems = filterNavItems(navItems);

  return (
    <nav
      aria-label="Main navigation"
      className={`p-2.5 transition-all duration-300 ${
        isOpen ? "w-full" : "w-20"
      }`}
    >
      {isOpen && (
        <div className="px-3 pt-1 pb-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Navigation</p>
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        {visibleItems.map((item) => renderMenuItem(item))}
      </div>
    </nav>
  );
};

export default SidebarMain;



